import {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
} from 'react'
import { toast } from 'react-toastify'

import { useMountEffect } from 'hooks/useMountEffect'
import {
  buildSpreadsheetCellRange,
  cellKey,
  formatCopyMatrix,
  isCellInSpreadsheetRange,
  moveCellWithinColumns,
  moveCellWithinRows,
  nextSelectionRangeForCell,
  parseSpreadsheetPaste,
  shouldUseDocumentSpreadsheetPaste,
  shouldWarnBeforeSpreadsheetUnload,
  valuesForPasteTarget,
} from './spreadsheetSelection'
import {
  focusSpreadsheetCell,
  isTargetInsideSpreadsheetRoot,
} from './spreadsheetDom'
import type {
  SpreadsheetCellCoordinate,
  SpreadsheetCellSaveState,
  SpreadsheetEditableCell,
  SpreadsheetSelectionRange,
} from './spreadsheetTypes'
import { useSpreadsheetSaveQueue } from './useSpreadsheetSaveQueue'

interface SpreadsheetContextValue<TRow> {
  activeCell: SpreadsheetCellCoordinate | null
  selection: SpreadsheetSelectionRange | null
  draftValue: string
  editing: boolean
  dragging: boolean
  selectDraftOnEdit: boolean
  saveStates: Record<string, SpreadsheetCellSaveState>
  registerCell: (cell: SpreadsheetEditableCell<TRow>) => void
  unregisterCell: (cell: SpreadsheetCellCoordinate) => void
  isCellSelected: (cell: SpreadsheetCellCoordinate) => boolean
  selectCell: (cell: SpreadsheetCellCoordinate, extend: boolean) => void
  moveActiveCell: (
    rowDelta: number,
    columnDelta?: number,
    extend?: boolean
  ) => void
  startDrag: (cell: SpreadsheetCellCoordinate) => void
  extendDrag: (cell: SpreadsheetCellCoordinate) => void
  endDrag: () => void
  beginEdit: (initialValue: string, options?: { selectDraft?: boolean }) => void
  setDraftValue: (value: string) => void
  cancelEdit: () => void
  clearActiveCell: () => void
  commitDraft: () => Promise<void>
  clearSelection: () => Promise<void>
  copySelection: () => Promise<void>
  pasteText: (text: string) => Promise<void>
}

const SpreadsheetContext =
  createContext<SpreadsheetContextValue<unknown> | null>(null)

function isTextEditablePasteTarget(target: EventTarget | null): boolean {
  if (!(target instanceof Element)) return false

  return Boolean(
    target.closest(
      [
        'input',
        'textarea',
        'select',
        '[contenteditable="true"]',
        '[role="textbox"]',
        '[role="combobox"]',
        '[role="dialog"]',
      ].join(',')
    )
  )
}

interface ProviderProps<TRow> {
  children: ReactNode
  orderedRows: TRow[]
  getRowId: (row: TRow) => string
}

export function DataTableSpreadsheetProvider<TRow>({
  children,
  orderedRows,
  getRowId,
}: ProviderProps<TRow>) {
  const cellsRef = useRef(new Map<string, SpreadsheetEditableCell<TRow>>())
  const [activeCell, setActiveCell] =
    useState<SpreadsheetCellCoordinate | null>(null)
  const [selection, setSelection] = useState<SpreadsheetSelectionRange | null>(
    null
  )
  const [draftValue, setDraftValue] = useState('')
  const [editing, setEditing] = useState(false)
  const [selectDraftOnEdit, setSelectDraftOnEdit] = useState(true)
  const [dragging, setDragging] = useState(false)
  const { pendingSaveCount, saveCells, saveStates } =
    useSpreadsheetSaveQueue<TRow>()
  const orderedRowIds = useMemo(
    () => orderedRows.map(getRowId),
    [orderedRows, getRowId]
  )

  const clearActiveState = useCallback(() => {
    setActiveCell(null)
    setSelection(null)
    setEditing(false)
    setSelectDraftOnEdit(true)
    setDraftValue('')
    setDragging(false)
  }, [])

  const registerCell = useCallback((cell: SpreadsheetEditableCell<TRow>) => {
    cellsRef.current.set(cellKey(cell.coordinate), cell)
  }, [])

  const unregisterCell = useCallback((cell: SpreadsheetCellCoordinate) => {
    cellsRef.current.delete(cellKey(cell))
  }, [])

  const editableColumnIdsForRow = useCallback(
    (rowIndex: number, rowId: string) =>
      Array.from(cellsRef.current.values())
        .filter(
          (cell) =>
            cell.coordinate.rowIndex === rowIndex &&
            cell.coordinate.rowId === rowId
        )
        .map((cell) => cell.coordinate.columnId),
    []
  )

  const selectedRange = useCallback(() => {
    if (!selection) return null

    return buildSpreadsheetCellRange(
      selection.anchor,
      selection.focus,
      editableColumnIdsForRow(selection.anchor.rowIndex, selection.anchor.rowId)
    )
  }, [editableColumnIdsForRow, selection])

  const selectedCellRows = useCallback(() => {
    const normalized = selectedRange()

    if (!normalized) {
      return activeCell
        ? [
            [cellsRef.current.get(cellKey(activeCell))].filter(
              (cell): cell is SpreadsheetEditableCell<TRow> => Boolean(cell)
            ),
          ].filter((row) => row.length > 0)
        : []
    }

    return orderedRows.flatMap((row, index) => {
      if (index < normalized.startRowIndex || index > normalized.endRowIndex) {
        return []
      }

      const rowCells = normalized.columnIds
        .slice(normalized.startColumnIndex, normalized.endColumnIndex + 1)
        .flatMap((columnId) => {
          const cell = cellsRef.current.get(
            cellKey({
              rowIndex: index,
              rowId: getRowId(row),
              columnId,
            })
          )

          return cell ? [cell] : []
        })

      return rowCells.length > 0 ? [rowCells] : []
    })
  }, [activeCell, getRowId, orderedRows, selectedRange])

  const selectedCells = useCallback(
    () => selectedCellRows().flat(),
    [selectedCellRows]
  )

  const isCellSelected = useCallback(
    (cell: SpreadsheetCellCoordinate) => {
      const normalized = selectedRange()

      if (!normalized) {
        return activeCell ? cellKey(activeCell) === cellKey(cell) : false
      }

      return isCellInSpreadsheetRange(cell, normalized)
    },
    [activeCell, selectedRange]
  )

  const pasteText = useCallback(
    async (text: string) => {
      const parsed = parseSpreadsheetPaste(text)
      const cells = selectedCells()

      if (parsed.mode === 'single') {
        const values = valuesForPasteTarget(parsed.values, cells.length)
        await saveCells(cells, (_cell, index) => values[index] ?? '')
        return
      }

      const normalized = selectedRange()
      const start =
        normalized?.startRowIndex ??
        cells[0]?.coordinate.rowIndex ??
        activeCell?.rowIndex
      const columnId =
        normalized?.columnIds[normalized.startColumnIndex] ??
        cells[0]?.coordinate.columnId ??
        activeCell?.columnId
      if (start === undefined || !columnId) return

      if (parsed.mode === 'matrix') {
        const startRow = orderedRows[start]
        if (!startRow) return

        const startRowId = getRowId(startRow)
        const editableColumnIds = Array.from(cellsRef.current.values())
          .filter(
            (cell) =>
              cell.coordinate.rowIndex === start &&
              cell.coordinate.rowId === startRowId
          )
          .map((cell) => cell.coordinate.columnId)
        const startColumnIndex = editableColumnIds.indexOf(columnId)
        if (startColumnIndex === -1) return

        const targetCells: Array<{
          cell: SpreadsheetEditableCell<TRow>
          value: string
        }> = []
        let skippedCells = 0
        parsed.rows.forEach((rowValues, rowOffset) => {
          const rowIndex = start + rowOffset
          const row = orderedRows[rowIndex]
          if (!row) {
            skippedCells += rowValues.length
            return
          }

          rowValues.forEach((value, columnOffset) => {
            const targetColumnId =
              editableColumnIds[startColumnIndex + columnOffset]
            const cell = targetColumnId
              ? cellsRef.current.get(
                  cellKey({
                    rowIndex,
                    rowId: getRowId(row),
                    columnId: targetColumnId,
                  })
                )
              : undefined
            if (cell) {
              targetCells.push({ cell, value })
            } else {
              skippedCells += 1
            }
          })
        })

        if (skippedCells > 0) {
          toast.warning(
            `Skipped ${skippedCells} pasted cell${
              skippedCells === 1 ? '' : 's'
            } outside the editable range.`,
            { toastId: 'spreadsheet-paste-skipped' }
          )
        }

        await saveCells(
          targetCells.map((target) => target.cell),
          (_cell, index) => targetCells[index]?.value ?? ''
        )
        return
      }

      const pasteCells = orderedRows
        .slice(start, start + parsed.values.length)
        .flatMap((row, offset) => {
          const cell = cellsRef.current.get(
            cellKey({
              rowIndex: start + offset,
              rowId: getRowId(row),
              columnId,
            })
          )

          return cell ? [cell] : []
        })

      const values = valuesForPasteTarget(parsed.values, pasteCells.length)
      await saveCells(pasteCells, (_cell, index) => values[index] ?? '')
    },
    [activeCell, getRowId, orderedRows, saveCells, selectedCells, selectedRange]
  )

  const activeCellRef = useRef(activeCell)
  const editingRef = useRef(editing)
  const pasteTextRef = useRef(pasteText)
  const clearActiveStateRef = useRef(clearActiveState)
  const shouldWarnBeforeUnloadRef = useRef(false)
  activeCellRef.current = activeCell
  editingRef.current = editing
  pasteTextRef.current = pasteText
  clearActiveStateRef.current = clearActiveState
  shouldWarnBeforeUnloadRef.current = shouldWarnBeforeSpreadsheetUnload(
    editing,
    pendingSaveCount
  )

  useMountEffect(() => {
    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      if (!shouldWarnBeforeUnloadRef.current) return

      event.preventDefault()
      event.returnValue = ''
    }

    window.addEventListener('beforeunload', handleBeforeUnload)
    return () => window.removeEventListener('beforeunload', handleBeforeUnload)
  })

  useMountEffect(() => {
    const handlePointerDown = (event: PointerEvent) => {
      if (isTargetInsideSpreadsheetRoot(event.target) || editingRef.current) {
        return
      }

      clearActiveStateRef.current()
    }

    document.addEventListener('pointerdown', handlePointerDown, true)
    return () =>
      document.removeEventListener('pointerdown', handlePointerDown, true)
  })

  useMountEffect(() => {
    const handleDocumentPaste = (event: ClipboardEvent) => {
      if (
        !shouldUseDocumentSpreadsheetPaste({
          defaultPrevented: event.defaultPrevented,
          editing: editingRef.current,
          hasActiveCell: Boolean(activeCellRef.current),
          targetInsideSpreadsheet: isTargetInsideSpreadsheetRoot(event.target),
          targetIsTextEditable: isTextEditablePasteTarget(event.target),
        })
      ) {
        return
      }

      const text = event.clipboardData?.getData('text/plain') ?? ''
      if (!text) return

      event.preventDefault()
      void pasteTextRef.current(text)
    }

    document.addEventListener('paste', handleDocumentPaste)
    return () => document.removeEventListener('paste', handleDocumentPaste)
  })

  const value = useMemo<SpreadsheetContextValue<TRow>>(
    () => ({
      activeCell,
      selection,
      draftValue,
      editing,
      dragging,
      selectDraftOnEdit,
      saveStates,
      registerCell,
      unregisterCell,
      isCellSelected,
      selectCell: (cell, extend) => {
        setActiveCell(cell)
        setSelection((prev) => nextSelectionRangeForCell(prev, cell, extend))
      },
      moveActiveCell: (rowDelta, columnDelta = 0, extend = false) => {
        if (!activeCell) return

        const nextCell =
          columnDelta === 0
            ? moveCellWithinRows(activeCell, rowDelta, orderedRowIds)
            : moveCellWithinColumns(
                activeCell,
                columnDelta,
                Array.from(cellsRef.current.values())
                  .filter(
                    (cell) =>
                      cell.coordinate.rowIndex === activeCell.rowIndex &&
                      cell.coordinate.rowId === activeCell.rowId
                  )
                  .map((cell) => cell.coordinate.columnId)
              )
        if (!nextCell || !cellsRef.current.has(cellKey(nextCell))) return

        setActiveCell(nextCell)
        setSelection((prev) =>
          nextSelectionRangeForCell(prev, nextCell, extend)
        )
        focusSpreadsheetCell(nextCell)
      },
      startDrag: (cell) => {
        setDragging(true)
        setActiveCell(cell)
        setSelection({ anchor: cell, focus: cell })
      },
      extendDrag: (cell) => {
        setSelection((prev) => {
          if (!dragging || !prev) {
            return prev
          }

          return nextSelectionRangeForCell(prev, cell, true)
        })
      },
      endDrag: () => setDragging(false),
      beginEdit: (initialValue, options) => {
        setDraftValue(initialValue)
        setSelectDraftOnEdit(options?.selectDraft ?? true)
        setEditing(true)
      },
      setDraftValue,
      cancelEdit: () => {
        setEditing(false)
        setSelectDraftOnEdit(true)
        setDraftValue('')
        focusSpreadsheetCell(activeCell)
      },
      clearActiveCell: clearActiveState,
      commitDraft: async () => {
        const cells = selectedCells()
        const valueToSave = draftValue
        setEditing(false)
        setSelectDraftOnEdit(true)
        setDraftValue('')
        focusSpreadsheetCell(activeCell)
        await saveCells(cells, () => valueToSave)
      },
      clearSelection: async () => {
        await saveCells(selectedCells(), () => '')
      },
      copySelection: async () => {
        const rows = selectedCellRows()
        await navigator.clipboard.writeText(
          formatCopyMatrix(
            rows.map((cells) =>
              cells.map((cell) => cell.meta.getValue(cell.row))
            )
          )
        )
      },
      pasteText,
    }),
    [
      activeCell,
      draftValue,
      dragging,
      editing,
      orderedRowIds,
      pasteText,
      registerCell,
      saveCells,
      saveStates,
      selectDraftOnEdit,
      isCellSelected,
      selectedCellRows,
      selectedCells,
      selection,
      unregisterCell,
      clearActiveState,
    ]
  )

  return (
    <SpreadsheetContext.Provider
      value={value as SpreadsheetContextValue<unknown>}
    >
      {children}
    </SpreadsheetContext.Provider>
  )
}

export function useDataTableSpreadsheet<TRow>() {
  return useContext(SpreadsheetContext) as SpreadsheetContextValue<TRow> | null
}
